/**
 * Burrows.
 *
 * Kate: "there could be a button where you can dig. You can dig like a tunnel, and
 * that's where you sleep so other animals, if you're playing multiplayer, can't kill
 * you." And on whether every animal can: "Yes, but you cannot go into the other
 * player's [burrow]. You can if you're the same animal though."
 *
 * So: any animal digs one, you're safe inside, and entry by anyone else is refused
 * unless they are the same species as the owner.
 */
import type { SpeciesId } from './species'

/** Radius of a burrow mouth in metres. */
export const BURROW_RADIUS = 1.6
/** Seconds of holding dig before the burrow exists. */
export const DIG_SECONDS = 2.5

export interface Burrow {
  id: number
  x: number
  z: number
  /** Who dug it — entry is species-gated on this. */
  ownerId: string
  ownerSpecies: SpeciesId
}

export class BurrowField {
  private readonly burrows: Burrow[] = []
  private nextId = 1

  get all(): readonly Burrow[] {
    return this.burrows
  }

  dig(x: number, z: number, ownerId: string, ownerSpecies: SpeciesId): Burrow {
    const burrow: Burrow = { id: this.nextId++, x, z, ownerId, ownerSpecies }
    this.burrows.push(burrow)
    return burrow
  }

  /** The burrow whose mouth contains this point, if any. */
  at(x: number, z: number): Burrow | null {
    for (const burrow of this.burrows) {
      if (Math.hypot(burrow.x - x, burrow.z - z) <= BURROW_RADIUS) return burrow
    }
    return null
  }

  /**
   * Kate's rule exactly: your own burrow always; someone else's only if you are the
   * same animal they are.
   */
  canEnter(burrow: Burrow, entererId: string, entererSpecies: SpeciesId): boolean {
    if (burrow.ownerId === entererId) return true
    return burrow.ownerSpecies === entererSpecies
  }

  remove(id: number): void {
    const index = this.burrows.findIndex((b) => b.id === id)
    if (index >= 0) this.burrows.splice(index, 1)
  }

  clear(): void {
    this.burrows.length = 0
  }
}
